import React from "react";
import AutocompleteSelect from "./AutocompleteSelect";
import DatePickerField from "./DatePickerField";
import TextFieldInput from "./TextFieldInput";
import AsyncAutocomplete from "./AsyncAutocomplete";
import NumberFieldInput from "./NumberFieldInput";
import MultiColumnAsyncAutocomplete from "./MultiColumnAsyncAutocomplete";
import ButtonField from "./ButtonField";
import CheckboxGroupRHF from "./CheckboxGroupRHF";
import MultiEmailInputField from "./MultiEmailInputField";
import EmailFieldInput from "./EmailFieldInput";
import AttachmentFieldInput from "./AttachmentFieldInput";
import CheckboxFieldRHF from "./CheckboxFieldRHF";
import RadioButtonRHF from "./RadioButtonRHF";
import { InputLabel } from "@material-ui/core";
import { useGlobalStyles } from "./globalStyles";
import TableField from "./TableField";
import IconRendererRHF from "./IconRendererRHF";
import RegexTextFieldInput from "./RegexTextFieldInput";
import SelectMultipleCreate from "./SelectMultipleCreate";
import NestedTable from "./NestedTable";

const FieldRenderer = ({ field, control }) => {
  const classes = useGlobalStyles();
  if (field.render && typeof field.render === 'function') {
    return field.render(field, { control });
  }
  const {
    type,
    name,
    label,
    required,
    options,
    disabled = false,
    format = "DD-MM-YYYY",
    views = ["year", "month", "day"],
    fetchOptions,
    labelKey = "",
    labelKeyArr = [],
    valueKey = "",
    columns = [],
    rows = [],
    minDate = null,
    maxDate = null,
    dependentFieldName = "",
    dependencies = [],
    onClick,
    fullWidth = false,
    sx,
    multiple = false,
    centerColumns = false,
    restrictRemove = false,
    varchar = 50,
    numberDigits = "20,10",
    size = "medium",
    maxHeight = 500,
    acceptAttachType = ".pdf,.doc,.docx,image/*",
    enableBlurHandler = false,
    onBlurErrorMsg = "",
    onCustomBlur,
    isLoading,
    loading = false,
    value,
    hasAddNew = false,
    tooltipMessage,
    tooltipIcon,
    max,
    min,
    alignRight = false,
    icons = [],
    pattern,
    patternErrorMsg,
    transform,
    component,
    allowSpecChar,
    optionHeight = 200,
    removeZeroTrim,
    isTableView = false,
    datesCalendar = [],
    config = [],
    onMonthChange,
    onOpen,
    clearable = false,
    resolveDayInfo,
    stickyHeader = false,
    hasFooter = false,
    footerTemplate,
    footerAlignColumn,
    noDataText,
    disablePortal = true,
    tableProps = {},
  } = field;
  switch (type) {
    case "autocomplete":
      return (
        <AutocompleteSelect //dropdown with UI search
          name={name}
          label={label}
          control={control}
          options={options}
          required={required}
          labelprop={labelKey}
          disabled={disabled}
          multiple={multiple}
          restrictRemove={restrictRemove}
          hasAddNew={hasAddNew}
          optionHeight={optionHeight}
          disablePortal={disablePortal}
          onOpen={onOpen}
        />
      );
    case "selectMultipleCreate":
      return (
        <SelectMultipleCreate
          name={name}
          control={control}
          label={label}
          required={required}
          options={options}
          fetchOptions={fetchOptions}
          labelKey={labelKey}
          columns={columns}
          disabled={disabled}
          isTableView={isTableView}
          dependencies={dependencies}
          multiple={multiple}
        />
      );
    case "text":
      return (
        <TextFieldInput
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
          multiline={field.multiline}
          rows={field.rows}
          minRows={field.minRows}
          maxRows={field.maxRows}
          varchar={varchar}
          size={size}
          enableBlurHandler={enableBlurHandler}
          onBlurErrorMsg={onBlurErrorMsg}
          onCustomBlur={onCustomBlur}
          pattern={pattern}
          patternErrorMsg={patternErrorMsg}
          transform={transform}
          placeholder={field.placeholder}
        />
      );
    case "label":
      return (
        <InputLabel className={classes.colorBlack} style={alignRight ? { textAlign: "right" } : {}}>
          {value}
        </InputLabel>
      );
    case "hidden":
      return (
        <TextFieldInput
          name={name}
          label={label}
          control={control}
          sx={{ display: "none" }}
        />
      );
    case "number":
      return (
        <NumberFieldInput
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
          varchar={numberDigits}
          size={size}
          tooltipMessage={tooltipMessage}
          tooltipIcon={tooltipIcon}
          max={max}
          min={min}
          removeZeroTrim={removeZeroTrim}
          onCustomBlur={onCustomBlur}
          placeholder={field.placeholder}
        />
      );
    case "button":
      return (
        <ButtonField
          label={label}
          onClick={onClick}
          fullWidth={fullWidth}
          disabled={disabled}
          sx={sx}
        />
      );
    case "table":
      return (
        <TableField
          rows={rows}
          columns={columns}
          control={control}
          name={name}
          maxHeight={maxHeight}
          loading={loading}
          stickyHeader={stickyHeader}
          hasFooter={hasFooter}
          footerTemplate={footerTemplate}
          footerAlignColumn={footerAlignColumn}
          noDataText={noDataText}
        />
      );
    case "nestedTable":
      return (
        <NestedTable
          data={rows}
          isLoading={loading}
          control={control}
          name={name}
          {...tableProps}
        />
      );
    case "date":
      return (
        <DatePickerField
          name={name}
          control={control}
          disabled={disabled}
          label={label}
          required={required}
          minDate={minDate}
          maxDate={maxDate}
          format={format}
          views={views}
          datesCalendar={datesCalendar}
          config={config}
          onMonthChange={onMonthChange}
          clearable={clearable}
          resolveDayInfo={resolveDayInfo}
        />
      );
    case "asyncAutocomplete":
      return (
        <AsyncAutocomplete // async autocomplete with api call
          name={name}
          label={label}
          control={control}
          required={required}
          fetchOptions={fetchOptions}
          labelKey={labelKey}
          valueKey={valueKey}
          disabled={disabled}
          dependentFieldName={dependentFieldName}
          hasAddNew={hasAddNew}
          columns={columns}
        />
      );
    case "multiasyncAutocomplete":
      return (
        <MultiColumnAsyncAutocomplete // async multicolumn autocomplete  with api call
          name={name}
          label={label}
          control={control}
          required={required}
          fetchOptions={fetchOptions}
          labelKeyArr={labelKeyArr}
          labelKey={labelKey}
          valueKey={valueKey}
          disabled={disabled}
          dependentFieldName={dependentFieldName}
          multiple={multiple}
          centerColumns={centerColumns}
        />
      );
    case "multiCheckbox":
      return (
        <CheckboxGroupRHF
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
          options={options}
        />
      );
    case "checkbox":
      return (
        <CheckboxFieldRHF
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
          options={options}
        />
      );
    case "multipleEmailsInput":
      return (
        <MultiEmailInputField
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
        />
      );
    case "email":
      return (
        <EmailFieldInput
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
        />
      );
    case "attachment":
      return (
        <AttachmentFieldInput
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
          accept={acceptAttachType}
          acceptErrorMsg={field.acceptErrorMsg}
        />
      );
    case "radioButton":
      return (
        <RadioButtonRHF
          name={name}
          control={control}
          label={label}
          required={required}
          options={options}
        />
      );
    case "icon":
      return (
        <IconRendererRHF icons={icons} />
      );
    case "regexText":
      return (
        <RegexTextFieldInput
          disabled={disabled}
          name={name}
          control={control}
          label={label}
          required={required}
          size={size}
          allowSpecChar={allowSpecChar}
          sx={sx}
        />
      );
    case "customComponent":
      return <React.Fragment>{component}</React.Fragment>;
    case "render":
      return field.render ? field.render(field) : null;
    default:
      return null;
  }
};

export default FieldRenderer;